import './gift-card-card.css';

import EventEmitter from 'eventemitter3';
import { Util } from '../../../utils/util';
import { IOrderGiftCard } from '../../../models/order.interface';
import { EventTypes } from '../../../core/event-bus';
import { Order } from '../../../models/order';
import { Data } from '../../../models/data';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Represents a UI component for displaying and managing a gift card in an order.
 */
export class GiftCardCard {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _giftCardNumber: string;
    private readonly _containerElement: HTMLElement | null;

    /**
     * Initializes a new instance of the GiftCardCard component.
     * @param {EventEmitter} eventBus - The global event bus for communication.
     * @param {Order} order - The order instance where the gift card is applied.
     * @param {string} giftCardNumber - The unique number of the gift card.
     * @param {Function} onDelete - The callback function triggered when deleting the gift card.
     */
    constructor(eventBus: EventEmitter, data: Data, localizationService: LocalizationService, order: Order, giftCardNumber: string, onDelete: Function) {
        this._eventBus = eventBus;
        this._data = data;
        this._localizationService = localizationService;
        this._order = order;
        this._giftCardNumber = giftCardNumber;
        this._containerElement = this._createGiftCardElement(onDelete);
        this._setupEventBusListeners();
    }

    /**
     * Gets the HTML element of the gift card component.
     * @returns {HTMLElement | null} The HTML element representing the gift card, or null if the card is not found.
     */
    public get element(): HTMLElement | null {
        return this._containerElement;
    }

    /**
     * Sets up event listeners for the gift card component.
     * @private
     */
    private _setupEventBusListeners(): void {
        this._eventBus.on(EventTypes.GIFT_CARD_CHANGED, this._updateGiftCardDetails);
    }

    /**
     * Creates the HTML structure for the gift card component.
     * @param {Function} onDelete - The callback function executed when removing the gift card.
     * @returns {HTMLElement | null} The created HTML element or null if the gift card is not found.
     * @private
     */
    private _createGiftCardElement(onDelete: Function): HTMLElement | null {
        const giftCard = this._findGiftCardInOrder();
        if (!giftCard)
            return null;

        const html = `
            <div id="div_checkout_applied_gift_${giftCard.randomId}" class="gift-card">
                <h2 class="gift-card-title" ltag="GiftCard">${this._localizationService.names("GiftCard")}</h2>
                <div class="gift-card-details-group">
                    <p class="gift-card-number">${giftCard.cardNo}</p>
                    <div class="gift-card-details">
                        ${this._generateGiftCardDetails(giftCard)}
                    </div>
                </div>
                <button id="btn_checkout_applied_gift_${giftCard.randomId}" class="remove-gift-card secondary" ltag="RemoveGiftCard">${this._localizationService.names("RemoveGiftCard")}</button>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const button = element.querySelector(`#btn_checkout_applied_gift_${giftCard.randomId}`) as HTMLButtonElement;
        if (button)
            button.onclick = () => { this._removeGiftCard(onDelete); };

        return element;
    }

    /**
     * Finds the gift card in the order by its number.
     * @returns {IOrderGiftCard | null} The matched gift card object or null if not found.
     * @private
     */
    private _findGiftCardInOrder(): IOrderGiftCard | null {
        return this._order.getProperty("GiftCards").find((x: IOrderGiftCard) => x.cardNo === this._giftCardNumber) || null;
    }

    /**
     * Generates the gift card details (balance, applied amount, and remaining balance).
     * @param {IOrderGiftCard} giftCard - The gift card object.
     * @returns {string} The HTML string containing formatted gift card details.
     * @private
     */
    private _generateGiftCardDetails(giftCard: IOrderGiftCard): string {
        const country = this._data.getProperty("StoreInfo").country;
        return `
            <p><span ltag="StartingBalance">${this._localizationService.names("StartingBalance")}</span>: ${Util.formatMoney(giftCard.balance, country)}</p>
            <p><span ltag="AppliedToOrder">${this._localizationService.names("AppliedToOrder")}</span>: ${Util.formatMoney(giftCard.appliedAmount, country)}</p>
            <p><span ltag="EndingBalance">${this._localizationService.names("EndingBalance")}</span>: ${Util.formatMoney(giftCard.balance - giftCard.appliedAmount, country)}</p>`;
    }

    /**
     * Removes the gift card from the order and unregisters event listeners.
     * @param {Function} onDelete - The callback function executed after removal.
     * @private
     */
    private readonly _removeGiftCard = (onDelete: Function): void => {
        this._eventBus.off(EventTypes.GIFT_CARD_CHANGED, this._updateGiftCardDetails);
        onDelete(this._giftCardNumber);
    }

    /**
     * Updates the displayed gift card details when the gift card data changes.
     * @private
     */
    private readonly _updateGiftCardDetails = (): void => {
        const giftCard = this._findGiftCardInOrder();
        if (!giftCard || !this._containerElement)
            return;

        const details = this._containerElement.querySelector(".gift-card-details") as HTMLElement;
        if (details)
            details.innerHTML = this._generateGiftCardDetails(giftCard);
    }
}